import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Badge, Box, IconButton, Link, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, Notifications } from '@mui/icons-material';
import NotificationMenuButtons from 'components/notifications/menu/NotificationMenuButtons';
import NotificationMenuItem from 'components/notifications/menu/NotificationMenuItem';
import NotificationMenuList from 'components/notifications/menu/NotificationMenuList';
import StyledNotificationFooter from 'common/theme/notification/StyledNotificationFooter';
import StyledNotificationHeader from 'common/theme/notification/StyledNotificationHeader';
import StyledNotificationPanel from 'common/theme/notification/StyledNotificationPanel';
import notificationApi from 'api/graphql/notification';
import routes from 'common/routes';
import EchoContext from 'context/EchoContext';
import useNotifications from 'hooks/notifications/useNotifications';
import useMenuNotifications from 'hooks/notifications/useMenuNotifications';
import {
  expiredNotificationMessage,
  getNotificationExpiry,
  resolveNotificationPath
} from 'common/utils/notificationNav';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

const anchorOrigin = {
  vertical: 'bottom',
  horizontal: 'right'
};

const transformOrigin = {
  vertical: 'top',
  horizontal: 'right'
};

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

const bellSx = {
  color: 'text.primary',
  transition: `transform 200ms ${EASE}`,
  '&:active': { transform: 'scale(0.92)' }
};

const seeAllSx = {
  borderRadius: 1.5,
  color: 'gold.main',
  display: 'inline-block',
  fontWeight: 700,
  px: 1.25,
  py: 0.5,
  transition: `background-color 200ms ${EASE}, transform 200ms ${EASE}`,
  '&:hover': { backgroundColor: 'surface.overlay' },
  '&:active': { transform: 'scale(0.98)' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2 }
};

const closeSx = {
  color: 'text.secondary',
  transition: `color 200ms ${EASE}, transform 200ms ${EASE}`,
  '&:hover': { color: 'text.primary' },
  '&:active': { transform: 'scale(0.92)' }
};

const badgeSx = {
  '& .MuiBadge-badge': {
    backgroundColor: 'gold.main',
    color: 'ink.main',
    fontSize: '0.625rem',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 800,
    height: 16,
    minWidth: 16
  }
};

function NotificationMenu({ anchorElement = null }) {
  const { t } = useTranslation(['notifications', 'buttons']);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user.id);
  const role = useSelector((state) => state.auth.user.role);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  // Borrado optimista: la fila desaparece al instante y el refresco llega después, para que
  // la bandeja no parpadee entre el clic y la respuesta del servidor.
  const [removedIds, setRemovedIds] = useState([]);
  const { echo } = useContext(EchoContext);
  const open = Boolean(anchorEl);

  const { listNotifications, notifications, refresh, status } = useMenuNotifications();

  const {
    countUnreadNotifications,
    getFormattedNotification,
    handleNotificationSideEffects,
    markNotificationAsRead,
    unreadNotificationsCount
  } = useNotifications();

  const visibleNotifications = useMemo(
    () => notifications.filter((notification) => !removedIds.includes(notification.id)),
    [notifications, removedIds]
  );
  const hasNotifications = visibleNotifications.length > 0;
  const panelTitle = t('menu.title', { ns: 'notifications', defaultValue: 'Notificaciones' });
  const closeLabel = t('close', { ns: 'buttons', defaultValue: 'Cerrar' });

  useEffect(() => {
    // Validate echo is available before subscribing
    if (!echo || !userId) {
      return undefined;
    }

    // Validate echo.private exists
    if (!echo.private || typeof echo.private !== 'function') {
      return undefined;
    }

    const channel = echo.private(`users.${userId}`);
    channel.notification((notification) => {
      const normalizedNotification = getFormattedNotification(notification);
      handleNotificationSideEffects(notification, normalizedNotification);
    });

    return () => {
      if (echo && typeof echo.leave === 'function') {
        echo.leave(`users.${userId}`);
      }
    };
  }, [echo, userId, getFormattedNotification, handleNotificationSideEffects]);

  useEffect(() => {
    if (userId) {
      listNotifications();
    }
  }, [listNotifications, refresh, userId]);

  useEffect(() => {
    if (userId) {
      countUnreadNotifications(userId);
    }
  }, [countUnreadNotifications, refresh, userId]);

  const onClick = () => {
    setAnchorEl(anchorElement);
  };

  const onClose = () => {
    setAnchorEl(null);
    setRemovedIds([]);
  };

  const notify = (variant, message) =>
    dispatch(
      uiSlice.actions.enqueueSnackbar(
        createSnack({ key: `notif-menu-${Date.now()}`, variant, message })
      )
    );

  // Clic en una notificación: marca leída + navega a su destino + cierra el panel.
  // El cierre va en el `finally` a propósito: si algo de aquí dentro lanza (un payload raro,
  // una ruta que no resuelve), el panel se cierra igual. Sin eso la bandeja se quedaba abierta
  // tapando el cambio y parecía que el clic "no hacía nada"; ningún ErrorBoundary lo captura,
  // porque solo envuelven el render, no los handlers.
  const handleItemClick = (notification) => {
    try {
      if (!notification.read) markNotificationAsRead(notification.id);

      // El evento ya pasó (sesión cumplida, premio vencido, ranking cerrado): avisamos en vez de
      // llevar al usuario a una pantalla donde no va a encontrar nada.
      const { expired, kind } = getNotificationExpiry(notification);
      if (expired) {
        notify('warning', expiredNotificationMessage(t, kind));
        return;
      }

      const path = resolveNotificationPath(notification, role);
      if (path) navigate(path);
    } finally {
      onClose();
    }
  };

  const handleDelete = async (notification) => {
    setRemovedIds((ids) => [...ids, notification.id]);

    try {
      const { data } = await notificationApi.remove(notification.id);
      if (!data?.deleteNotification?.success) throw new Error('deleteNotification failed');

      countUnreadNotifications(userId);
      listNotifications();
    } catch (error) {
      // Si el servidor rechazó el borrado la fila tiene que volver a la bandeja.
      setRemovedIds((ids) => ids.filter((id) => id !== notification.id));
      notify(
        'error',
        t('deleteError', {
          ns: 'notifications',
          defaultValue: 'No pudimos eliminar la notificación.'
        })
      );
    }
  };

  const renderItems = () =>
    visibleNotifications.map((notification) => (
      <NotificationMenuItem
        key={notification.id}
        notification={notification}
        expired={getNotificationExpiry(notification).expired}
        onItemClick={handleItemClick}
        onDelete={handleDelete}
      />
    ));

  return (
    <div>
      <IconButton
        id="notification-menu-button"
        aria-label={t('menu.open', { ns: 'notifications', defaultValue: 'Ver notificaciones' })}
        edge="start"
        aria-controls={open ? 'notification-menu' : undefined}
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : undefined}
        onClick={onClick}
        size={isSmall ? 'small' : 'medium'}
        sx={bellSx}>
        <Badge badgeContent={unreadNotificationsCount} max={99} overlap="circular" sx={badgeSx}>
          <Notifications />
        </Badge>
      </IconButton>
      <StyledNotificationPanel
        id="notification-menu"
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        open={open}
        onClose={onClose}
        PaperProps={{ 'aria-label': panelTitle, role: 'dialog' }}>
        <StyledNotificationHeader>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
              {panelTitle}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {unreadNotificationsCount > 0
                ? t('menu.unread', {
                    count: unreadNotificationsCount,
                    ns: 'notifications',
                    defaultValue: `${unreadNotificationsCount} sin leer`
                  })
                : t('menu.allRead', { ns: 'notifications', defaultValue: 'Estás al día' })}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flex: 'none' }}>
            <NotificationMenuButtons
              canClear={hasNotifications}
              unreadCount={unreadNotificationsCount}
            />
            {/* Cerrar explícito: hasta ahora el panel solo se cerraba tocando fuera, que en
                móvil no es un gesto evidente y dejaba la sensación de bandeja "pegada". */}
            <Tooltip title={closeLabel}>
              <IconButton size="small" aria-label={closeLabel} onClick={onClose} sx={closeSx}>
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </StyledNotificationHeader>

        <NotificationMenuList
          isValid={hasNotifications}
          onRetry={listNotifications}
          renderItems={renderItems}
          status={status}
        />

        {/* "Ver todas" → centro de notificaciones (/notifications, ya en el router). */}
        <StyledNotificationFooter>
          <Link
            component={RouterLink}
            to={routes.notifications.path}
            onClick={onClose}
            underline="none"
            variant="body2"
            sx={seeAllSx}>
            {t('menu.seeAll', { ns: 'notifications', defaultValue: 'Ver todas' })}
          </Link>
        </StyledNotificationFooter>
      </StyledNotificationPanel>
    </div>
  );
}

NotificationMenu.propTypes = {
  anchorElement: PropTypes.instanceOf(Element)
};

export default NotificationMenu;
